import React, { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { Icon } from '@iconify/react';

interface Props {
  name: string;
  className?: string; // allow overriding size when needed
}

const ProfileImageUpload: React.FC<Props> = ({ name, className = 'w-28 h-28' }) => {
  const { setFieldValue, values } = useFormikContext<any>();
  const formValue = values?.[name] ?? '';
  const [preview, setPreview] = useState<string>(formValue || '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // keep local preview in sync with Formik value (useful when form is reinitialized)
  useEffect(() => {
    setPreview(formValue || '');
  }, [formValue]);

  // Revoke blob URL when preview changes or component unmounts
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    // revoke previous blob if any
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setPreview(url);
    // store preview URL in form value (for display) and store the File separately for upload
    setFieldValue(name, url);
    setFieldValue(`${name}File`, file);
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setPreview('');
    setFieldValue(name, '');
    setFieldValue(`${name}File`, null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  return (
    <div
      className={`relative ${className} rounded-full bg-gray-100 overflow-hidden flex items-center justify-center border border-gray-200 group`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={0}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openFilePicker();
        }}
        className="absolute inset-0 flex items-center justify-center cursor-pointer"
      >
        {preview ? (
          // preview image
          <img src={preview} alt="Profile preview" className="object-cover w-full h-full" />
        ) : (
          // default user icon
          <Icon icon={'mdi:account'} className="w-3/4 h-3/4 text-gray-300" />
        )}

        {/* overlay on hover */}
        <div className="absolute inset-0 flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 bg-black bg-opacity-30 z-10">
          <div className="flex flex-col items-center justify-center gap-1 text-white text-center px-2">
            <Icon icon={'mdi:camera-outline'} className="w-6 h-6" />
            <span className="text-sm leading-none">Click to change photo</span>
          </div>
        </div>
      </div>

      {/* remove button */}
      {(preview || formValue) && (
        <button
          type="button"
          aria-label="Remove image"
          onClick={handleRemove}
          className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow border border-gray-200 text-gray-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ProfileImageUpload;
